// =======================================
// 3D background orbits + restored planet, behind your content
// =======================================
import { useEffect, useRef } from 'react';
import '../styles/VideoSkin.css';

export default function VisualSkin(): null {
  const backRef = useRef(null);
  const frontRef = useRef(null);
  const backCanvasRef = useRef(null);
  const frontCanvasRef = useRef(null);
  const planetRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('video-skin');

    // Overlays
    const back = document.createElement('div');
    back.className = 'vs-overlay-back';
    const front = document.createElement('div');
    front.className = 'vs-overlay-front';

    // Planet + logo
    const planet = document.createElement('div');
    planet.className = 'vs-planet';
    const logoBox = document.createElement('div');
    logoBox.className = 'vs-logo';
    planet.appendChild(logoBox);

    // Canvases
    const canvasBack = document.createElement('canvas');
    canvasBack.className = 'vs-stars';
    const canvasFront = document.createElement('canvas');
    canvasFront.className = 'vs-stars';

    back.appendChild(planet);
    back.appendChild(canvasBack);
    front.appendChild(canvasFront);
    document.body.appendChild(back);
    document.body.appendChild(front);

    backRef.current = back;
    frontRef.current = front;
    backCanvasRef.current = canvasBack;
    frontCanvasRef.current = canvasFront;
    planetRef.current = planet;

    // Env
    const reduce = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const ctxB = canvasBack.getContext('2d', { alpha: true });
    const ctxF = canvasFront.getContext('2d', { alpha: true });

    // CSS settings
    const css = getComputedStyle(document.documentElement);
    // Detect dark/light mode for particle blending
    const isDarkMode = document.body.getAttribute('data-theme') === 'dark';

    // Derive particle color from the theme's --primary-color (hex) as an RGB triplet
    const COLOR = (() => {
      const hex = (css.getPropertyValue('--primary-color') || '').trim();
      if (hex && hex.startsWith('#') && hex.length >= 7) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) return `${r}, ${g}, ${b}`;
      }
      return '244, 162, 97'; // Warm Amber fallback (matches Midnight Plum primary)
    })();
    const SIZE_VAR =
      parseFloat(css.getPropertyValue('--size-variance')) || 0.28;
    const ALPHA_VAR =
      parseFloat(css.getPropertyValue('--alpha-variance')) || 0.3;
    const USER_DENSITY = (() => {
      const v = parseFloat(css.getPropertyValue('--vs-density'));
      return Number.isFinite(v) && v > 0 ? Math.min(1, Math.max(0.1, v)) : 1;
    })();
    const LOGO_AMP = parseFloat(css.getPropertyValue('--logo-float-amp')) || 10; // px
    const LOGO_PERIOD_S =
      parseFloat(
        (css.getPropertyValue('--logo-float-period') || '').replace('s', '')
      ) || 6;

    // Device factor for particle density
    const mem =
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 4;
    const deviceFactor = Math.min(1, (mem / 4) * (1.5 / DPR));
    const density = deviceFactor * USER_DENSITY;

    // Particle caps
    const BASE_BASELINE = 520;
    const BASELINE = Math.floor(BASE_BASELINE * density);
    const MAX_COUNT = Math.floor(2400 * density);
    let activeN = Math.max(200, BASELINE);

    // Scene state (planet fixed; logo floats)
    const state = {
      w: 0,
      h: 0,
      W: 0,
      H: 0,
      cx: 0,
      cy: 0,
      theta: 0,
      omega: (Math.PI * 2) / 36,
      mx: 0,
      my: 0,
      pmx: 0,
      pmy: 0,
      pointerSpeed: 0,
      xAmpScale: 1,
      burstTime: 0,
      burstSign: +1,
      rxOrbit: 0,

      // logo float
      thetaL: 0,
      omegaL: (Math.PI * 2) / Math.max(1, LOGO_PERIOD_S), // rad/s
      logoAmp: LOGO_AMP,
    };

    // Sprites
    const SPRITE_BUCKETS = 8;
    const spriteDot = new Array(SPRITE_BUCKETS);
    const spriteSq = new Array(SPRITE_BUCKETS);
    function buildSprites(maxSizePx: number) {
      for (let i = 0; i < SPRITE_BUCKETS; i++) {
        const t = (i + 1) / SPRITE_BUCKETS;
        const r = Math.max(1, Math.round(maxSizePx * t));
        {
          // dot
          const off = document.createElement('canvas');
          off.width = off.height = r * 2 + 2;
          const c = off.getContext('2d');
          c.fillStyle = `rgb(${COLOR})`;
          c.beginPath();
          c.arc(r + 1, r + 1, r, 0, Math.PI * 2);
          c.fill();
          spriteDot[i] = off;
        }
        {
          // square
          const off = document.createElement('canvas');
          const s = r * 2;
          off.width = off.height = s + 2;
          const c = off.getContext('2d');
          c.fillStyle = `rgb(${COLOR})`;
          c.fillRect(1, 1, s, s);
          spriteSq[i] = off;
        }
      }
    }

    // Particles
    const phase = new Float32Array(MAX_COUNT);
    const base = new Float32Array(MAX_COUNT);
    const kind = new Uint8Array(MAX_COUNT);
    const xAmp = new Float32Array(MAX_COUNT);
    const xPhase = new Float32Array(MAX_COUNT);
    const y0 = new Float32Array(MAX_COUNT);
    const vAmp = new Float32Array(MAX_COUNT);
    const vPhase = new Float32Array(MAX_COUNT);
    const fbPhase = new Float32Array(MAX_COUNT);

    // Frame buffers
    const idxBack = new Uint32Array(MAX_COUNT);
    const idxFront = new Uint32Array(MAX_COUNT);
    const bufX = new Float32Array(MAX_COUNT);
    const bufY = new Float32Array(MAX_COUNT);
    const bufS = new Float32Array(MAX_COUNT);
    const bufA = new Float32Array(MAX_COUNT);
    const bufB = new Uint8Array(MAX_COUNT);
    const bufK = kind;

    // Resize/init
    let resizeRAF = 0;
    const doResize = () => {
      state.w = window.innerWidth;
      state.h = window.innerHeight;
      state.W = Math.floor(state.w * DPR);
      state.H = Math.floor(state.h * DPR);
      state.cx = state.w * 0.5;
      state.cy = state.h * 0.5;

      canvasBack.width = state.W;
      canvasBack.height = state.H;
      canvasFront.width = state.W;
      canvasFront.height = state.H;

      // Circle fixed at center
      planet.style.left = '50%';
      planet.style.top = '50%';
      planet.style.transform = 'translate(-50%,-50%)';

      buildSprites(Math.max(1, Math.round(3 * DPR)));

      state.rxOrbit = state.w * 0.52;

      for (let i = 0; i < MAX_COUNT; i++) {
        y0[i] = Math.random() * state.h;
        xAmp[i] = (0.05 + Math.random() * 0.18) * state.w;
        xPhase[i] = Math.random() * Math.PI * 2;
        vAmp[i] = (0.005 + Math.random() * 0.03) * state.h;
        vPhase[i] = Math.random() * Math.PI * 2;
        fbPhase[i] = Math.random() * Math.PI * 2;
        phase[i] = Math.random() * Math.PI * 2;
        base[i] = (1 + Math.random() * 2.0) * DPR;
        kind[i] = Math.random() < 0.22 ? 1 : 0;
      }
    };
    const onResize = () => {
      if (resizeRAF) cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(doResize);
    };
    doResize();
    window.addEventListener('resize', onResize, { passive: true });

    // Interactivity
    let lastMoveT = performance.now();
    const onMove = (e: MouseEvent) => {
      const now = performance.now();
      const dt = Math.max(1, now - lastMoveT);
      lastMoveT = now;
      const dx = e.clientX - state.pmx;
      const dy = e.clientY - state.pmy;
      const speed = Math.hypot(dx, dy) / dt;
      state.pointerSpeed = state.pointerSpeed * 0.85 + speed * 0.15;
      state.pmx = state.mx = e.clientX;
      state.pmy = state.my = e.clientY;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    const onDown = (e: MouseEvent) => {
      state.burstSign = e.button === 2 || e.altKey ? -1 : +1;
      state.burstTime = 600;
    };
    const onTouch = () => {
      state.burstSign = +1;
      state.burstTime = 600;
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('contextmenu', (e) => e.preventDefault());
    window.addEventListener('touchstart', onTouch, { passive: true });

    if (reduce) {
      return () => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mousedown', onDown);
        window.removeEventListener('touchstart', onTouch);
        document.body.classList.remove('video-skin');
        back.remove();
        front.remove();
      };
    }

    // Animation + autoscale + logo float
    let acc = 0,
      tPrev = performance.now();
    const STEP = 1000 / 60;
    let emaDt = 16.7,
      timeSinceTune = 0;

    function frame(now = performance.now()) {
      const frameDt = Math.min(100, now - tPrev);
      tPrev = now;
      acc += frameDt;

      // perf
      emaDt = emaDt * 0.9 + frameDt * 0.1;
      timeSinceTune += frameDt;

      // pointer effect
      const ampBoost = 1 + Math.min(0.6, state.pointerSpeed * 0.9);
      const omegaBoost = 1 + Math.min(0.5, state.pointerSpeed * 0.7);
      state.xAmpScale = ampBoost;

      while (acc >= STEP) {
        state.theta += state.omega * omegaBoost * (STEP / 1000); // particles
        state.thetaL += state.omegaL * (STEP / 1000); // logo float
        if (state.burstTime > 0) state.burstTime -= STEP;
        acc -= STEP;
      }

      if (timeSinceTune > 500) {
        timeSinceTune = 0;
        if (emaDt > 18 && activeN > 200)
          activeN = Math.max(200, Math.floor(activeN * 0.9));
        else if (emaDt < 16.2 && activeN < MAX_COUNT)
          activeN = Math.min(MAX_COUNT, Math.floor(activeN * 1.1));
      }

      // === Logo floating transform ===
      {
        const y = state.logoAmp * Math.sin(state.thetaL);
        // derive “velocity” for subtle squash/stretch
        const vy = state.logoAmp * Math.cos(state.thetaL) * state.omegaL;
        const spd = Math.min(1, Math.abs(vy) / 3); // normalized
        const sx = 1 + -0.03 * spd;
        const sy = 1 + 0.03 * spd;
        // why: translateY keeps it centered while floating; scale adds bounce feel
        logoBox.style.transform = `translateY(${y.toFixed(2)}px) scale(${sx.toFixed(3)}, ${sy.toFixed(3)}) translateZ(0)`;
      }

      // === Particles ===
      ctxB.clearRect(0, 0, canvasBack.width, canvasBack.height);
      ctxF.clearRect(0, 0, canvasFront.width, canvasFront.height);
      // 'lighter' adds color channels — great for dark backgrounds (brightens subtly),
      // but causes blown-out blobs on light backgrounds. Use source-over for light mode.
      const blendMode = isDarkMode ? 'lighter' : 'source-over';
      ctxB.globalCompositeOperation = blendMode as GlobalCompositeOperation;
      ctxF.globalCompositeOperation = blendMode as GlobalCompositeOperation;

      const { cx, theta, mx, my, rxOrbit } = state;
      const R = Math.max(80, Math.min(state.cx, state.cy) * 0.45);
      const invR2 = 1 / (R * R);
      const burstK =
        state.burstTime > 0 ? state.burstSign * (state.burstTime / 600) : 0;

      let nBack = 0,
        nFront = 0;
      for (let i = 0; i < activeN; i++) {
        const ang = theta + phase[i];

        let x = cx + rxOrbit * Math.cos(ang);
        let y = y0[i] + vAmp[i] * Math.sin(ang * 0.5 + vPhase[i]);
        x += xAmp[i] * state.xAmpScale * Math.sin(ang + xPhase[i]);

        const dx = mx - x,
          dy = my - y;
        const d2 = dx * dx + dy * dy;
        const w = 1 / (1 + d2 * invR2);
        const k = 0.18 * w + 0.28 * w * burstK;
        x += dx * k;
        y += dy * k * 0.6;
        x += (mx - cx) * 0.008;

        const frontness = Math.sin(ang + fbPhase[i]) + w * 0.9;

        const sizePx = Math.max(0.6, base[i] * (1 + SIZE_VAR * frontness));
        const alpha = Math.min(
          1,
          Math.max(0.12, 0.25 + ALPHA_VAR * (frontness * 0.5 + 0.5))
        );
        const norm = Math.min(1, sizePx / (3 * DPR));
        const bucket = Math.max(
          0,
          Math.min(SPRITE_BUCKETS - 1, (norm * (SPRITE_BUCKETS - 1)) | 0)
        );

        if (frontness < 0) {
          idxBack[nBack++] = i;
        } else {
          idxFront[nFront++] = i;
        }
        bufX[i] = x * DPR;
        bufY[i] = y * DPR;
        bufS[i] = sizePx;
        bufA[i] = alpha;
        bufB[i] = bucket;
      }

      for (let k = 0; k < nBack; k++) {
        const i = idxBack[k];
        ctxB.globalAlpha = bufA[i];
        const bmp = bufK[i] === 1 ? spriteSq[bufB[i]] : spriteDot[bufB[i]];
        const hw = bmp.width * 0.5,
          hh = bmp.height * 0.5;
        ctxB.drawImage(bmp, bufX[i] - hw, bufY[i] - hh);
      }
      for (let k = 0; k < nFront; k++) {
        const i = idxFront[k];
        ctxF.globalAlpha = bufA[i];
        const bmp = bufK[i] === 1 ? spriteSq[bufB[i]] : spriteDot[bufB[i]];
        const hw = bmp.width * 0.5,
          hh = bmp.height * 0.5;
        ctxF.drawImage(bmp, bufX[i] - hw, bufY[i] - hh);
      }

      requestAnimationFrame(frame);
    }

    const rafe = requestAnimationFrame(frame);

    // Cleanup
    return () => {
      cancelAnimationFrame(rafe);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('touchstart', onTouch);
      document.body.classList.remove('video-skin');
      back.remove();
      front.remove();
    };
  }, []);

  return null;
}
